import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, Plus, Edit, Trash2, Loader2, Eye, Users,
  CheckCircle, Clock, Upload, Link2, Download, X, LayoutTemplate
} from 'lucide-react';
import DocumentFieldEditor from '../../components/DocumentFieldEditor';

export default function AdminDocumentsPage() {
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    requires_student_signature: true,
    requires_parent_signature: true,
    initials_fields: [],
    section_ids: [],
    file: null,
  });
  const fileInputRef = useRef();

  // Field editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDoc, setEditorDoc] = useState(null);

  // Signatures dialog
  const [sigsOpen, setSigsOpen] = useState(false);
  const [sigsDoc, setSigsDoc] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [sigsLoading, setSigsLoading] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchSections();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const r = await api.get('/documents/');
      setDocuments(r.data);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      const r = await api.get('/sections/?page_size=200&include_private=true');
      setSections(r.data.results || r.data);
    } catch {}
  };

  const openUploadDialog = (doc = null) => {
    setEditingDoc(doc);
    if (doc) {
      setForm({
        title: doc.title,
        description: doc.description || '',
        requires_student_signature: doc.requires_student_signature,
        requires_parent_signature: doc.requires_parent_signature,
        section_ids: doc.section_ids || [],
        file: null,
      });
    } else {
      setForm({
        title: '',
        description: '',
        requires_student_signature: true,
        requires_parent_signature: true,
        section_ids: [],
        file: null,
      });
    }
    setUploadOpen(true);
  };

  const toggleSection = (sectionId) => {
    setForm(f => ({
      ...f,
      section_ids: f.section_ids.includes(sectionId)
        ? f.section_ids.filter(id => id !== sectionId)
        : [...f.section_ids, sectionId],
    }));
  };

  const openFieldEditor = (doc) => {
    setEditorDoc(doc);
    setEditorOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!editingDoc && !form.file) { toast.error('Please select a PDF file'); return; }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('title', form.title);
      data.append('description', form.description);
      data.append('requires_student_signature', form.requires_student_signature);
      data.append('requires_parent_signature', form.requires_parent_signature);
      form.section_ids.forEach(id => data.append('section_ids', id));
      if (form.file) data.append('file', form.file);

      if (editingDoc) {
        await api.patch(`/documents/${editingDoc.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Document updated');
      } else {
        await api.post('/documents/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Document uploaded');
      }

      setUploadOpen(false);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save document');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (doc) => {
    const data = new FormData();
    data.append('is_active', !doc.is_active);
    try {
      await api.patch(`/documents/${doc.id}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(doc.is_active ? 'Document deactivated' : 'Document activated');
      fetchDocuments();
    } catch {
      toast.error('Failed to update document status');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Permanently delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc.id}/`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDeleteSignature = async (sig) => {
    if (!window.confirm('Delete this signature record? The parent will need to re-sign.')) return;
    try {
      await api.delete(`/documents/signatures/${sig.id}/`);
      toast.success('Signature deleted');
      setSignatures(prev => prev.filter(s => s.id !== sig.id));
      setDocuments(prev => prev.map(d =>
        d.id === sigsDoc?.id ? { ...d, signature_count: Math.max(0, (d.signature_count || 1) - 1) } : d
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete signature');
    }
  };

  const downloadPdf = async (sigId, title, parentName, learnerName) => {
    try {
      const r = await api.get(`/documents/signatures/${sigId}/download/`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const parts = [title, parentName, learnerName].filter(Boolean).join('_').replace(/\s+/g, '_');
      a.download = `${parts || 'signed-document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const openSignatures = async (doc) => {
    setSigsDoc(doc);
    setSigsOpen(true);
    setSigsLoading(true);
    try {
      const r = await api.get(`/documents/${doc.id}/signatures/`);
      setSignatures(r.data);
    } catch {
      toast.error('Failed to load signatures');
    } finally {
      setSigsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Agreement Documents</h1>
          <p className="text-muted-foreground">Upload PDF agreements and link them to sessions. Parents must sign before paying.</p>
        </div>
        <Button onClick={() => openUploadDialog()}>
          <Plus className="h-4 w-4 mr-2" /> Upload Document
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-1">No documents yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Upload your first agreement document to get started.</p>
            <Button onClick={() => openUploadDialog()}>
              <Upload className="h-4 w-4 mr-2" /> Upload Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Linked Sessions</TableHead>
                <TableHead>Requires</TableHead>
                <TableHead>Signatures</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map(doc => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <div className="font-medium">{doc.title}</div>
                    {doc.description && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{doc.description}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {(doc.section_ids?.length || 0) + (doc.camp_section_ids?.length || 0)} session(s)
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {doc.requires_student_signature && <Badge variant="outline" className="text-xs">Student</Badge>}
                      {doc.requires_parent_signature && <Badge variant="outline" className="text-xs">Parent</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => openSignatures(doc)}
                      className="flex items-center gap-1 text-primary hover:underline text-sm"
                    >
                      <Users className="h-3 w-3" />
                      {doc.signature_count || 0} signed
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={doc.is_active}
                        onCheckedChange={() => handleToggleActive(doc)}
                      />
                      <span className="text-sm text-muted-foreground">
                        {doc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {doc.file_url && (
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" title="View PDF">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openUploadDialog(doc)} title="Edit">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openFieldEditor(doc)} title="Place signature fields">
                        <LayoutTemplate className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(doc)} title="Delete document" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Upload / Edit Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDoc ? 'Edit Document' : 'Upload Agreement Document'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input
                placeholder="e.g. FLL Rules & Expectations Handbook"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of this document..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="space-y-1">
              <Label>PDF File {editingDoc ? '(leave blank to keep existing)' : '*'}</Label>
              <div
                className="border-2 border-dashed border-muted rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {form.file ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <FileText className="h-5 w-5 text-primary" />
                    <span>{form.file.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, file: null })); }}>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    <Upload className="h-8 w-8 mx-auto mb-2" />
                    Click to select a PDF file
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.requires_student_signature}
                  onCheckedChange={v => setForm(f => ({ ...f, requires_student_signature: v }))}
                />
                <Label>Student signature required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.requires_parent_signature}
                  onCheckedChange={v => setForm(f => ({ ...f, requires_parent_signature: v }))}
                />
                <Label>Parent signature required</Label>
              </div>
            </div>

            {/* Section linking */}
            <div className="space-y-2">
              <Label>Link to Sessions</Label>
              <p className="text-xs text-muted-foreground">Select which sessions require this document before payment.</p>
              <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
                {sections.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">No sessions available</p>
                ) : (
                  sections.map(s => (
                    <label key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={form.section_ids.includes(s.id)}
                        onChange={() => toggleSection(s.id)}
                      />
                      <span>{s.course_title || s.title || `Section ${s.id.slice(0, 8)}`}</span>
                      {s.term && <span className="text-muted-foreground">· {s.term}</span>}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={uploading}>
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingDoc ? 'Save Changes' : 'Upload Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field placement editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-[96vw] h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4" />
              Place Signature Fields — {editorDoc?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {editorDoc && (
              <DocumentFieldEditor
                doc={editorDoc}
                onClose={() => setEditorOpen(false)}
                onSaved={(fields) => {
                  setDocuments(prev => prev.map(d => d.id === editorDoc.id ? { ...d, fields } : d));
                  setEditorOpen(false);
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Signatures viewer */}
      <Dialog open={sigsOpen} onOpenChange={setSigsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Signatures — {sigsDoc?.title}</DialogTitle>
          </DialogHeader>
          {sigsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : signatures.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No signatures yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Learner</TableHead>
                  <TableHead>Parent Signature</TableHead>
                  <TableHead>Signed At</TableHead>
                  <TableHead>PDF</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {signatures.map(sig => (
                  <TableRow key={sig.id}>
                    <TableCell>{sig.learner_name || '—'}</TableCell>
                    <TableCell>{sig.parent_signature || '—'}</TableCell>
                    <TableCell>
                      {sig.completed_at
                        ? new Date(sig.completed_at).toLocaleDateString()
                        : <span className="text-muted-foreground text-xs">Incomplete</span>}
                    </TableCell>
                    <TableCell>
                      {sig.signed_pdf_url ? (
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(sig.id, sigsDoc?.title, sig.parent_print_name, sig.learner_name)}>
                          <Download className="h-3 w-3 mr-1" /> PDF
                        </Button>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteSignature(sig)} title="Delete signature" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
